import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Avatar from "../common/Avatar";
import BottomSheet from "../common/BottomSheet";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import messageRequestService from "../../services/messageRequestApi";

const firstNameOf = (name) => (name || "").trim().split(/\s+/)[0] || name || "";

// DeactivateTimerModal — the thread-level timer deactivation moment (design frame
// 1n; locked at THREAD level in Turn 6, D-011). Deliberate friction for a
// permanent decision: only THIS thread's chip flips to ∞ — the scope card makes
// that physical by showing the struck "Nd left" → "∞ Permanent" and an explicit
// "Other & future threads … keep their timers" row.
//
// Props:
//   participant  { name, subtitle?, profile_picture_url? } — the counterpart
//                (subtitle = "Nordic Eleven · Stockholm" from design 1n).
//   thread       { intentLabel, originLabel, currentTimerLabel } — e.g.
//                { intentLabel:"Networking", originLabel:"Direct",
//                  currentTimerLabel:"3d left" }.
//   channelCid   the Stream cid the timer is keyed by (D-011).
//   onClose      () => void
//   onConfirm    optional async (channelCid) => {success,error}. When omitted the
//                modal calls messageRequestService.deactivateTimer itself.
//   onDeactivated optional () => void — fired after a successful deactivation so
//                the caller can refresh the timer chip.
//   t            i18n passthrough (identity default = verbatim copy).
const DeactivateTimerModal = ({
  participant,
  thread,
  channelCid,
  onClose,
  onConfirm,
  onDeactivated,
  t = (s) => s,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Below md the modal presents as a bottom sheet (design frame 1w); the sheet
  // manages its own ESC/scroll-lock, so wire the portal's key/scroll handling
  // only on desktop.
  const isMobile = useMediaQuery("(max-width: 767px)");

  useEffect(() => {
    if (isMobile) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [onClose, isMobile]);

  if (!participant) return null;

  const first = firstNameOf(participant.name);
  const threadLine = [thread?.intentLabel, thread?.originLabel]
    .filter(Boolean)
    .join(" · ");

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = onConfirm
      ? await onConfirm(channelCid)
      : await messageRequestService.deactivateTimer(channelCid);
    setBusy(false);
    if (res?.success) {
      onDeactivated?.();
      onClose?.();
    } else {
      setError(res?.error || t("Could not deactivate the timer. Please try again."));
    }
  };

  // Shared body — the ∞ tile, headline, scope card and footnote. Rendered inside
  // the desktop portal card and the mobile bottom sheet alike (design 1n/1w).
  const body = (
    <div className="flex flex-col" style={{ gap: 15 }}>
        {/* ∞ tile */}
        <div
          className="flex items-center justify-center"
          style={{ width: 44, height: 44, borderRadius: 12, background: "#EAF2FF" }}
        >
          <span className="text-[20px] font-bold leading-none text-[#1466D8]">∞</span>
        </div>

        <div className="flex flex-col gap-1.5">
          {/* The sheet chrome carries this headline on mobile — desktop only. */}
          {!isMobile && (
            <span className="text-[16.5px] font-bold tracking-[-0.01em] text-[#101828]">
              {t("Deactivate timer for this thread?")}
            </span>
          )}
          <span className="text-[13px] leading-[1.55] text-[#475467]">
            {t("This thread will")}{" "}
            <span className="font-bold text-[#101828]">{t("no longer expire")}</span>.{" "}
            {t("Other and future threads with")} {first} {t("keep their own timers.")}
          </span>
        </div>

        {/* Scope card */}
        <div
          className="flex flex-col gap-2.5"
          style={{ border: "1px solid #EDF0F4", background: "#F8FAFC", borderRadius: 12, padding: "12px 14px" }}
        >
          <div className="flex items-center gap-2.5">
            <Avatar
              src={participant.profile_picture_url}
              alt={participant.name}
              fallbackText={participant.name}
              size={32}
            />
            <div className="flex flex-col">
              <span className="text-[12.5px] font-bold text-[#101828]">
                {participant.name}
              </span>
              {participant.subtitle && (
                <span className="text-[11px] text-[#667085]">{participant.subtitle}</span>
              )}
            </div>
          </div>

          <div className="h-px" style={{ background: "#EDF0F4" }} />

          {/* This thread: Nd left → ∞ Permanent */}
          <div className="flex items-center justify-between">
            <span className="text-[11.5px] text-[#475467]">{threadLine}</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold">
              {thread?.currentTimerLabel && (
                <span
                  className="text-[#B54708]"
                  style={{ textDecoration: "line-through", textDecorationThickness: "1.5px" }}
                >
                  {thread.currentTimerLabel}
                </span>
              )}
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6h8M7 3l3 3-3 3"
                  stroke="#98A2B3"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-[#17803D]">{t("∞ Permanent")}</span>
            </span>
          </div>

          {/* Unchanged: other & future threads */}
          <div className="flex items-center justify-between">
            <span className="text-[11.5px] text-[#475467]">
              {t("Other & future threads with")} {first}
            </span>
            <span className="text-[11px] font-semibold text-[#475467]">
              {t("Keep their timers")}
            </span>
          </div>
        </div>

        <span className="text-[11.5px] leading-[1.5] text-[#98A2B3]">
          {/* T036: the old copy read "<name> isn't notified." full stop, while
              deactivation posts a system line BOTH sides read ("The club turned
              off the expiry timer for this chat…", chat_timer.deactivate_timer)
              and that line becomes the counterpart's row preview. The bell half
              of the promise holds — no notification fires — so the fix is to
              state both halves. The chat line itself stays: in-thread
              transparency is deliberate and other timer flows emit the same
              kind of line. The reactivate sentence is now genuinely backed by
              the ⋯-menu entry shipped in T034, so it keeps its wording. */}
          {first}{" "}
          {t(
            "isn't notified, but this chat will show a line that the timer was turned off. You can reactivate the timer for this thread anytime."
          )}
        </span>

        {error && <span className="text-[11.5px] text-[#B42318]">{error}</span>}
    </div>
  );

  const actions = (
    <div className="flex justify-end gap-2.5">
      <button
        type="button"
        onClick={onClose}
        disabled={busy}
        className="rounded-[9px] text-[13px] font-semibold text-[#475467] disabled:opacity-60"
        style={{ padding: "9px 16px", background: "#FFFFFF", border: "1px solid #E4E9F0" }}
      >
        {t("Cancel")}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={busy}
        className="rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-60"
        style={{ padding: "9px 16px", background: "#2283FF", boxShadow: "0 1px 2px rgba(34,131,255,.35)" }}
      >
        {busy ? t("Deactivating…") : t("Deactivate timer")}
      </button>
    </div>
  );

  // Mobile: bottom sheet (design frame 1w). Same body + actions, sheet chrome.
  if (isMobile) {
    return (
      <BottomSheet
        open
        onClose={onClose}
        title={t("Deactivate timer for this thread?")}
        footer={actions}
      >
        <div className="pb-1">{body}</div>
      </BottomSheet>
    );
  }

  // Desktop: centered portal card (design 1n).
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div
        className="relative flex flex-col bg-white"
        style={{
          width: 396,
          maxWidth: "100%",
          borderRadius: 16,
          padding: 24,
          gap: 15,
          boxShadow: "0 24px 64px rgba(9,14,26,.45)",
        }}
      >
        {body}
        {actions}
      </div>
    </div>,
    document.body,
  );
};

export default DeactivateTimerModal;
